import type { ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import type {
    IScoringRuleDto,
    ScoringConfigRule,
    ScoringConfigType,
} from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { hasAnyScoringRules } from "app/pages/sales-intelligence/domain/leads/utils/hasAnyScoringRules";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { ScoringScopeBadgeKeys } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringScopeBadge";
import { SCORING_EXPORT_TRANSLATION_KEYS } from "app/pages/sales-intelligence/modules/lead-scoring-agent/constants/translations";
import { useExportScoringRules } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useExportScoringRules";
import { useScoringBoardTrackingService } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardTrackingService";
import type { ScoringScopeSegment } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringRulesView";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import type { SignalByIdMap } from "app/pages/sales-intelligence/sub-modules/signals/types/extended-models";

type ScoringRulesExportButtonProps = {
    /** The rules of the active segment — the file mirrors what the board currently shows. */
    rules: ScoringConfigRule;
    getRuleScope: (rule: IScoringRuleDto) => ScoringConfigType | undefined;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    signalById: SignalByIdMap;
    scopeBadgeKeys?: ScoringScopeBadgeKeys;
    /** Tracked so we know which segment users take out of the product. */
    segment: ScoringScopeSegment;
    automationPrefix: string;
};

export const ScoringRulesExportButton = ({
    rules,
    getRuleScope,
    filtersList,
    signalById,
    scopeBadgeKeys,
    segment,
    automationPrefix,
}: ScoringRulesExportButtonProps): ReactElement => {
    const { translate } = useTranslate();
    const scoringBoardTracking = useScoringBoardTrackingService();
    const { exportRules, isExporting } = useExportScoringRules({
        rules,
        getRuleScope,
        filtersList,
        signalById,
        scopeBadgeKeys,
    });

    const isExportToExcelDisabled = !hasAnyScoringRules(rules) || isExporting;

    const handleClick = () => {
        scoringBoardTracking.trackExportRules(segment);
        exportRules();
    };

    return (
        <Tooltip
            enabled={!isExportToExcelDisabled}
            content={translate(SCORING_EXPORT_TRANSLATION_KEYS.tooltip)}
        >
            <IconButton
                type="flat"
                iconName="excel-download"
                isLoading={isExporting}
                isDisabled={isExportToExcelDisabled}
                onClick={handleClick}
                dataAutomation={`${automationPrefix}-export`}
            />
        </Tooltip>
    );
};
